import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, Table2, UtensilsCrossed, ShoppingCart, TrendingUp, Users, LogOut, Archive, Settings, BellRing, Calendar, Package } from 'lucide-react';
import apiClient, { clearAuthToken } from '../utils/api';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'sonner';

let globalNotifiedTables = [];
let globalNotifiedOrders = [];

const playNotificationSound = () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(523.25, ctx.currentTime);
    gain1.gain.setValueAtTime(0.5, ctx.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(ctx.currentTime);
    osc1.stop(ctx.currentTime + 0.2);

    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(659.25, ctx.currentTime + 0.15);
    gain2.gain.setValueAtTime(0.5, ctx.currentTime + 0.15);
    gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(ctx.currentTime + 0.15);
    osc2.stop(ctx.currentTime + 0.5);
  } catch (e) {
    console.error("Audio play failed", e);
  }
};

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  let userType = 'employee';
  let userPermissions = ['orders'];
  const userName = localStorage.getItem('user_name') || localStorage.getItem('business_name');

  const token = localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
  if (token) {
    try {
      const decoded = jwtDecode(token);
      userType = decoded.user_type || 'employee';
      if (userType === 'owner') {
        userPermissions = ['all'];
      } else {
        let perms = decoded.permissions || ['orders'];
        // Legacy mapping expansion
        const expandedPerms = new Set(perms);
        if (perms.includes('tables')) {
          expandedPerms.add('view_tables').add('manage_table_status').add('add_table').add('delete_table');
        }
        if (perms.includes('orders')) {
          expandedPerms.add('view_orders').add('checkout_orders').add('print_receipt').add('view_past_orders').add('print_past_receipt');
        }
        if (perms.includes('revenue')) {
          expandedPerms.add('view_dashboard');
        }
        userPermissions = Array.from(expandedPerms);
      }
    } catch (e) {
      console.error('Invalid token');
      clearAuthToken();
      window.location.href = '#/login';
    }
  }

  const [activeWaiterCalls, setActiveWaiterCalls] = useState(0);
  const [activeOrdersCount, setActiveOrdersCount] = useState(0);
  const [activeReservationsCount, setActiveReservationsCount] = useState(0);
  const [tenantInfo, setTenantInfo] = useState({
    business_name: localStorage.getItem('business_name') || 'Cularix POS',
    business_name_color: localStorage.getItem('business_name_color') || '#f97316',
    reservation_enabled: localStorage.getItem('reservation_enabled') === 'true',
    inventory_enabled: localStorage.getItem('inventory_enabled') === 'true'
  });

  // Apply theme on initial load
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    if (savedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  useEffect(() => {
    if (!token) return;

    let isFirstCheck = true;

    const fetchTenantData = async () => {
      try {
        const response = await apiClient.get('/tenant/settings');
        const isResEnabled = response.data.reservation_enabled ?? false;
        localStorage.setItem('reservation_enabled', isResEnabled ? 'true' : 'false');
        const isInvEnabled = response.data.inventory_enabled ?? false;
        localStorage.setItem('inventory_enabled', isInvEnabled ? 'true' : 'false');
        localStorage.setItem('business_name_color', response.data.business_name_color || '#f97316');
        setTenantInfo({
          business_name: response.data.business_name || 'Cularix POS',
          business_name_color: response.data.business_name_color || '#f97316',
          reservation_enabled: isResEnabled,
          inventory_enabled: isInvEnabled
        });
      } catch (e) {}
    };
    
    fetchTenantData();

    const checkNotifications = async () => {
      try {
        // Waiter calls
        const tablesRes = await apiClient.get('/tables');
        const tables = tablesRes.data;
        const currentlyCalling = tables.filter(t => t.waiter_called).map(t => t.table_number);
        setActiveWaiterCalls(currentlyCalling.length);
        
        currentlyCalling.forEach(tableNum => {
          if (!globalNotifiedTables.includes(tableNum)) {
            playNotificationSound();
            toast.error(`Masa ${tableNum} Garson Çağırıyor!`, { duration: 10000 });
          }
        });
        globalNotifiedTables = currentlyCalling;
        
        // New orders
        const ordersRes = await apiClient.get('/orders');
        const pendingOrders = ordersRes.data.filter(o => o.status === 'pending');
        
        setActiveOrdersCount(pendingOrders.length);
        
        // New reservations
        try {
          const resRes = await apiClient.get('/reservations');
          const pendingRes = resRes.data.filter(r => r.status === 'pending');
          setActiveReservationsCount(pendingRes.length);
        } catch (e) {}

        if (isFirstCheck) {
          globalNotifiedOrders = pendingOrders.map(o => o.id);
          isFirstCheck = false;
        } else {
          pendingOrders.forEach(order => {
            if (!globalNotifiedOrders.includes(order.id)) {
              playNotificationSound();
              toast.success(`Masa ${order.table_number} Yeni Sipariş!`, { duration: 10000 });
              globalNotifiedOrders.push(order.id);
            }
          });
        }
        
      } catch (error) {}
    };

    checkNotifications();
    const interval = setInterval(checkNotifications, 10000);
    return () => clearInterval(interval);
  }, [token]);

  const handleLogout = () => {
    clearAuthToken();
    localStorage.removeItem('admin_email');
    localStorage.removeItem('admin_username');
    localStorage.removeItem('user_type');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_name');
    localStorage.removeItem('business_name');
    localStorage.removeItem('tenant_id');
    navigate('/login');
  };

  // Define menu items based on role
  // Define menu items based on role
  const allMenuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', permission: 'view_dashboard' },
    { path: '/admin/tables', icon: Table2, label: 'Masalar', permission: 'view_tables' },
    { path: '/admin/reservations', icon: Calendar, label: 'Rezervasyonlar', permission: 'reservations' },
    { path: '/admin/menu', icon: UtensilsCrossed, label: 'Menü', permission: 'menu' },
    { path: '/admin/inventory', icon: Package, label: 'Stok Yönetimi', permission: 'menu' },
    { path: '/admin/orders', icon: ShoppingCart, label: 'Siparişler', permission: 'view_orders' },
    { path: '/admin/past-orders', icon: Archive, label: 'Geçmiş Siparişler', permission: 'view_past_orders' },
    { path: '/admin/revenue', icon: TrendingUp, label: 'Gelir Raporu', permission: 'revenue' },
    { path: '/admin/employees', icon: Users, label: 'Çalışanlar', permission: 'employees' },
    { path: '/admin/settings', icon: Settings, label: 'Ayarlar', permission: 'settings' },
  ];

  // Permission checking is now handled by the userPermissions array directly.
  
  // Filter menu items based on permissions and reservation/inventory enabled setting
  const menuItems = allMenuItems.filter(item => {
    if (item.path === '/admin/reservations' && !tenantInfo.reservation_enabled) {
      return false;
    }
    if (item.path === '/admin/inventory' && !tenantInfo.inventory_enabled) {
      return false;
    }
    return item.permission === 'all' || 
      userPermissions.includes('all') || 
      userPermissions.includes(item.permission);
  });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="flex">
        <aside className="w-64 min-h-screen bg-white dark:bg-slate-900/50 border-r border-slate-200 dark:border-slate-800 p-6">
          <div className="mb-8">
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: tenantInfo.business_name_color }}>
              {tenantInfo.business_name}
            </h2>
            <p className="text-slate-500 text-sm mt-1">
              {userType === 'owner' ? 'Sahip' : 'Çalışan'}
            </p>
            <p className="text-slate-600 text-xs mt-1">{userName}</p>
          </div>

          <nav className="space-y-2">
            {menuItems.map((item) => (
              <Button
                key={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                onClick={() => navigate(item.path)}
                variant="ghost"
                className={`w-full justify-start relative ${
                  location.pathname === item.path
                    ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:bg-slate-800 hover:text-slate-800 dark:text-slate-200'
                }`}
              >
                <item.icon size={20} className="mr-3" />
                {item.label}
                {item.label === 'Masalar' && activeWaiterCalls > 0 && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 bg-red-500 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full animate-pulse">
                    {activeWaiterCalls}
                  </span>
                )}
                {item.label === 'Siparişler' && activeOrdersCount > 0 && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 bg-red-500 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full animate-pulse">
                    {activeOrdersCount}
                  </span>
                )}
                {item.label === 'Rezervasyonlar' && activeReservationsCount > 0 && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 bg-purple-600 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full animate-bounce">
                    {activeReservationsCount}
                  </span>
                )}
              </Button>
            ))}
          </nav>

          <div className="absolute bottom-6">
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-red-400 hover:bg-red-500/20 hover:text-red-300"
            >
              <LogOut size={20} className="mr-3" />
              Çıkış Yap
            </Button>
          </div>
        </aside>

        <main className="flex-1 p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;